import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';

interface SecureHtmlProps {
  content: string;
  className?: string;
  allowedTags?: string[];
  allowedAttributes?: string[];
}

const defaultAllowedTags = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'a', 'blockquote', 'div', 'span', 'code', 'pre',
  'sub', 'sup', 'mark', 'del', 'ins', 'small'
];

const defaultAllowedAttributes = [
  'href', 'target', 'rel', 'class', 'style', 'color', 'background-color',
  'text-align', 'font-size', 'font-weight', 'font-style', 'text-decoration',
  'dir', 'align'
];

/**
 * SecureHtml component that sanitizes HTML content before rendering
 * Prevents XSS attacks by removing dangerous HTML elements and attributes
 */
export const SecureHtml = ({ 
  content, 
  className,
  allowedTags = defaultAllowedTags,
  allowedAttributes = defaultAllowedAttributes
}: SecureHtmlProps) => {
  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttributes,
      FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
      FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur'],
      ALLOW_DATA_ATTR: false,
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    });
  }, [content, allowedTags, allowedAttributes]);

  return (
    <div 
      className={cn("prose max-w-none", className)}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

/**
 * More restrictive version for terms and agreements
 */
export const SecureTermsHtml = ({ content, className }: Omit<SecureHtmlProps, 'allowedTags' | 'allowedAttributes'>) => {
  return (
    <SecureHtml
      content={content}
      className={className}
      allowedTags={['p', 'br', 'strong', 'b', 'em', 'i', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'div']}
      allowedAttributes={['class']}
    />
  );
};